import {
  CanvasTexture,
  Group,
  LinearFilter,
  SRGBColorSpace,
  Sprite,
  SpriteMaterial,
} from "three";
import type { Aircraft } from "../aircraft/Aircraft";
import type { AirportMap, Vec2 } from "../airport/AirportDefinition";
import { LAYER } from "../config/palette";
import type { LandingIntent } from "../simulation/RouteSystem";
import type { AirportRenderer } from "./AirportRenderer";
import { type AircraftEntry, AircraftViewFactory } from "./AircraftView";
import { type AssetLoader, createThreeAssetLoader } from "./AssetLoader";
import { EffectsView } from "./EffectsView";
import { type DrawingLike, RouteView } from "./RouteView";
import { SceneryBuilder } from "./SceneryBuilder";

function createCrashMarker(): Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 32;
  const context = canvas.getContext("2d");
  if (context) {
    context.font = '26px "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText("🔥", 16, 17);
  }
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.magFilter = LinearFilter;
  texture.minFilter = LinearFilter;
  texture.generateMipmaps = false;

  const sprite = new Sprite(
    new SpriteMaterial({ map: texture, transparent: true, depthTest: false }),
  );
  sprite.name = "crash-fire";
  sprite.scale.set(10, 10, 1);
  sprite.renderOrder = 20;
  sprite.visible = false;
  return sprite;
}

/**
 * Owns everything inside the scene: static world, aircraft and flight paths.
 * The renderer owns the camera; this owns what the camera looks at.
 */
export class WorldView {
  private readonly root = new Group();
  private readonly aircraftGroup = new Group();
  private readonly scenery = new SceneryBuilder();
  private readonly routes = new RouteView();
  private readonly effects = new EffectsView();
  private readonly entries = new Map<number, AircraftEntry>();
  private readonly assetLoader: AssetLoader;
  private crashMarker: Sprite | null = null;
  private factory: AircraftViewFactory | null = null;
  private symbolsVisible = false;

  constructor(
    private readonly renderer: AirportRenderer,
    assetLoader: AssetLoader = createThreeAssetLoader(),
  ) {
    this.aircraftGroup.name = "aircraft";
    this.root.add(
      this.scenery.groundMesh,
      this.scenery.sceneryGroup,
      this.scenery.runwayGroup,
      this.effects.group,
      this.routes.group,
      this.aircraftGroup,
    );
    this.renderer.scene.add(this.root);
    this.assetLoader = assetLoader;
  }

  get modelReady(): boolean {
    return this.factory !== null;
  }

  async loadAssets(): Promise<void> {
    this.factory = new AircraftViewFactory({
      toy: await this.assetLoader.loadAircraftTemplate(),
    });
    // The models load after the first frame, so the mode set before then has
    // to be handed to the factory once it exists.
    this.factory.setSymbolsVisible(this.symbolsVisible);
  }

  build(map: AirportMap, nowSeconds: number): void {
    this.scenery.build(map, nowSeconds);
    this.scenery.setSymbolsVisible(this.symbolsVisible);
  }

  /** Colour-blind mode: shapes on the runway thresholds and the aircraft. */
  setSymbolsVisible(visible: boolean): void {
    this.symbolsVisible = visible;
    this.scenery.setSymbolsVisible(visible);
    this.factory?.setSymbolsVisible(visible);
  }

  /**
   * Dust off the wheels. A touchdown passes no heading and gets a ring; a
   * take-off passes the roll direction and gets a cone behind it.
   */
  spawnDust(position: Vec2, heading: number | null = null): void {
    this.effects.burst(position, heading);
  }

  /** Tyre marks down the runway, from the point the wheels bit. */
  spawnSkid(position: Vec2, heading: number): void {
    this.effects.skid(position, heading);
  }

  showCrash(position: Vec2): void {
    if (!this.crashMarker) {
      this.crashMarker = createCrashMarker();
      this.root.add(this.crashMarker);
    }
    this.crashMarker.position.set(position.x, position.y, LAYER.aircraft + 3);
    this.crashMarker.visible = true;
  }

  clearCrash(): void {
    if (this.crashMarker) {
      this.crashMarker.visible = false;
    }
  }

  syncAircraft(
    aircraft: readonly Aircraft[],
    highlightedId: number | null,
    warningIds: ReadonlySet<number>,
    time: number,
    paused = false,
    /**
     * How close each warned aircraft's worst encounter is, 0..1. Optional:
     * without it every ring is drawn at its widest, which is what the old
     * binary warning looked like.
     */
    warningLevels: ReadonlyMap<number, number> = new Map(),
  ): void {
    this.scenery.update(time, paused);
    this.effects.update(time, paused);
    if (!this.factory) {
      return;
    }

    const live = new Set<number>();
    for (const item of aircraft) {
      live.add(item.id);
      let entry = this.entries.get(item.id);
      if (!entry) {
        entry = this.factory.create(item.kind, item.airframe);
        entry.group.name = `aircraft-${item.id}`;
        entry.visualHeading = item.heading;
        this.aircraftGroup.add(entry.group);
        this.entries.set(item.id, entry);
      }
      this.factory.update(
        entry,
        item,
        time,
        item.id === highlightedId,
        warningIds.has(item.id),
        warningLevels.get(item.id) ?? 0,
      );
    }

    for (const [id, entry] of this.entries) {
      if (live.has(id)) {
        continue;
      }
      this.aircraftGroup.remove(entry.group);
      this.factory.disposeEntry(entry);
      this.entries.delete(id);
    }
  }

  syncPaths(
    aircraft: readonly Aircraft[],
    drawing: DrawingLike | null,
    highlightedId: number | null,
    intent: LandingIntent | null,
    time: number,
  ): void {
    this.routes.sync(aircraft, drawing, highlightedId, intent, time);
  }

  dispose(): void {
    this.renderer.scene.remove(this.root);
    if (this.crashMarker) {
      // Built lazily on the first crash, and holds a CanvasTexture nothing
      // else owns — `clearCrash` only hides it.
      this.crashMarker.material.map?.dispose();
      this.crashMarker.material.dispose();
      this.root.remove(this.crashMarker);
      this.crashMarker = null;
    }
    if (this.factory) {
      for (const entry of this.entries.values()) {
        this.factory.disposeEntry(entry);
      }
      this.factory.dispose();
    }
    this.entries.clear();
    this.effects.dispose();
    this.routes.dispose();
    this.scenery.dispose();
  }
}
